/**
 * Asset State Machines - XState machines for different asset types
 * 
 * Each asset type can have its own custom state machine with:
 * - Custom states (intact, armed, exploding, etc.)
 * - Custom transitions and guards
 * - Custom actions (sound effects, particle spawns, etc.)
 */

import { setup, assign } from 'xstate';
import { logger } from '../core/Logger';

/**
 * Shared context for all asset state machines
 */
interface AssetContext {
  id: string;
  assetId?: string;
  behaviorId?: string;
  totalDamage: number;
  breakThreshold: number;
  debrisLifetime: number;
  health: number;  // Current HP (starts at maxHealth)
  maxHealth: number;  // Maximum HP
}

/**
 * Generic dynamic object machine (fallback for unknown asset types)
 * States: intact → damaged → destroyed
 * Default: 10 HP, disappears at 0 HP
 */
export const genericDynamicObjectMachine = setup({
  types: {} as {
    context: AssetContext;
    input: Partial<AssetContext>;
    events:
      | { type: 'DAMAGE_RECEIVED'; amount: number }
      | { type: 'DESTROY' };
  },
  actions: {
    applyDamage: assign({
      health: ({ context, event }) => {
        if (event.type === 'DAMAGE_RECEIVED') {
          const newHealth = Math.max(0, context.health - event.amount);
          logger.assets.debug(`${context.id} took ${event.amount} damage, HP: ${newHealth}/${context.maxHealth}`);
          return newHealth;
        }
        return context.health;
      },
      totalDamage: ({ context, event }) => {
        if (event.type === 'DAMAGE_RECEIVED') {
          return context.totalDamage + event.amount;
        }
        return context.totalDamage;
      },
    }),
    logDestroyed: ({ context }) => {
      logger.assets.warn(`⚠️ Object ${context.id} destroyed (HP reached 0)`);
    },
    despawnObject: ({ context }) => {
      logger.assets.debug(`Despawning ${context.id}`);
      // Actual despawn logic will be handled by DynamicObjectSystem
    },
  },
  guards: {
    // Guard checks if applying this damage would destroy the object
    // Guards run BEFORE actions, so we need to compute the new health here
    isDestroyed: ({ context, event }) => {
      if (event.type === 'DAMAGE_RECEIVED') {
        return (context.health - event.amount) <= 0;
      }
      return context.health <= 0;
    },
  },
}).createMachine({
  id: 'genericDynamicObject',
  initial: 'intact',
  // XState v5: Use context factory to merge input with defaults
  context: ({ input }) => ({
    id: input?.id ?? 'unknown',
    behaviorId: input?.behaviorId,
    totalDamage: input?.totalDamage ?? 0,
    breakThreshold: input?.breakThreshold ?? 100,  // Legacy - kept for compatibility
    debrisLifetime: input?.debrisLifetime ?? 5,
    health: input?.health ?? 10,  // Default 10 HP for fallback
    maxHealth: input?.maxHealth ?? 10,
  }),
  states: {
    intact: {
      on: {
        DAMAGE_RECEIVED: [
          {
            actions: ['applyDamage'],
            target: 'destroyed',
            guard: 'isDestroyed',
          },
          {
            actions: ['applyDamage'],
            target: 'damaged',
          },
        ],
      },
    },
    damaged: {
      on: {
        DAMAGE_RECEIVED: [
          {
            actions: ['applyDamage'],
            target: 'destroyed',
            guard: 'isDestroyed',
          },
          {
            actions: ['applyDamage'],
          },
        ],
      },
    },
    destroyed: {
      entry: ['logDestroyed', 'despawnObject'],
      type: 'final',
    },
  },
});

/**
 * Wooden crate machine - similar to generic but with impact animations
 * States: intact → breaking → debris → cleanup
 */
export const crateWoodenMachine = setup({
  types: {} as {
    context: AssetContext;
    input: Partial<AssetContext>;
    events:
      | { type: 'DAMAGE_RECEIVED'; amount: number }
      | { type: 'IMPACT' }
      | { type: 'BREAK' }
      | { type: 'CLEANUP' };
  },
  actions: {
    recordDamage: assign({
      totalDamage: ({ context, event }) => {
        if (event.type === 'DAMAGE_RECEIVED') {
          return context.totalDamage + event.amount;
        }
        return context.totalDamage;
      },
      health: ({ context, event }) => {
        if (event.type === 'DAMAGE_RECEIVED') {
          const newHealth = Math.max(0, context.health - event.amount);
          logger.assets.debug(`Crate ${context.id} took ${event.amount} damage, HP: ${newHealth}/${context.maxHealth}`);
          return newHealth;
        }
        return context.health;
      },
    }),
    playImpactAnimation: ({ context }) => {
      logger.assets.debug(`Crate ${context.id} playing impact animation`);
    },
    playBreakAnimation: ({ context }) => {
      logger.assets.debug(`Crate ${context.id} playing break animation`);
    },
    hideRenderMesh: ({ context }) => {
      logger.assets.debug(`Hiding crate render mesh for ${context.id}`);
    },
    spawnDebris: ({ context }) => {
      logger.assets.debug(`Spawning wooden debris for ${context.id}`);
    },
    cleanup: ({ context }) => {
      logger.assets.debug(`Cleaning up crate ${context.id}`);
    },
  },
  guards: {
    canBreak: ({ context, event }) => {
      // Break when health would be reduced to 0 or below
      if (event.type === 'DAMAGE_RECEIVED') {
        return (context.health - event.amount) <= 0;
      }
      return context.health <= 0;
    },
  },
}).createMachine({
  id: 'crateWooden',
  initial: 'intact',
  // XState v5: Use context factory to merge input with defaults
  context: ({ input }) => ({
    id: input?.id ?? 'unknown',
    totalDamage: input?.totalDamage ?? 0,
    breakThreshold: input?.breakThreshold ?? 100,
    debrisLifetime: input?.debrisLifetime ?? 8,
    health: input?.health ?? 100,
    maxHealth: input?.maxHealth ?? 100,
  }),
  states: {
    intact: {
      on: {
        DAMAGE_RECEIVED: [
          {
            actions: ['recordDamage', 'playBreakAnimation'],
            target: 'breaking',
            guard: 'canBreak',
          },
          {
            actions: ['recordDamage', 'playImpactAnimation'],
          },
        ],
      },
    },
    breaking: {
      entry: ['hideRenderMesh', 'spawnDebris'],
      after: {
        0: 'debris',
      },
    },
    debris: {
      // Wait for DEBRIS_TIMEOUT event from DynamicObjectSystem
      // (which handles debris lifetime and fade-out properly)
      on: {
        CLEANUP: 'cleanup',
        DEBRIS_TIMEOUT: 'cleanup',
        FORCE_CLEANUP: 'cleanup',
      },
    },
    cleanup: {
      entry: ['cleanup'],
      type: 'final',
    },
  },
});

/**
 * Explosive barrel machine - has armed state with fuse timer
 * States: intact → armed → exploding → debris → cleanup
 *         armed can revert to intact if timer expires
 */
export const barrelExplosiveMachine = setup({
  types: {} as {
    context: AssetContext & { 
      fuseThreshold: number;
      armTime: number;
    };
    input: Partial<AssetContext & { fuseThreshold: number; armTime: number }>;
    events:
      | { type: 'DAMAGE_RECEIVED'; amount: number }
      | { type: 'EXPLODE' }
      | { type: 'DISARM' }
      | { type: 'CLEANUP' };
  },
  actions: {
    recordDamage: assign({
      totalDamage: ({ context, event }) => {
        if (event.type === 'DAMAGE_RECEIVED') {
          return context.totalDamage + event.amount;
        }
        return context.totalDamage;
      },
    }),
    startBeeping: ({ context }) => {
      logger.assets.warn(`Barrel ${context.id} armed! Beeping started.`);
    },
    stopBeeping: ({ context }) => {
      logger.assets.debug(`Barrel ${context.id} disarmed.`);
    },
    triggerExplosion: ({ context }) => {
      logger.assets.warn(`Barrel ${context.id} EXPLODING!`);
    },
    spawnExplosiveDebris: ({ context }) => {
      logger.assets.debug(`Spawning explosive debris for ${context.id}`);
    },
    hideRenderMesh: ({ context }) => {
      logger.assets.debug(`Hiding barrel render mesh for ${context.id}`);
    },
    cleanup: ({ context }) => {
      logger.assets.debug(`Cleaning up barrel ${context.id}`);
    },
  },
  guards: {
    shouldArm: ({ context }) => {
      return context.totalDamage >= 10;
    },
    shouldExplode: ({ context }) => {
      return context.totalDamage >= context.fuseThreshold;
    },
  },
}).createMachine({
  id: 'barrelExplosive',
  initial: 'intact',
  // XState v5: Use context factory to merge input with defaults
  context: ({ input }) => ({
    id: input?.id ?? 'unknown',
    totalDamage: input?.totalDamage ?? 0,
    breakThreshold: input?.breakThreshold ?? 100,
    debrisLifetime: input?.debrisLifetime ?? 15,
    health: input?.health ?? 100,
    maxHealth: input?.maxHealth ?? 100,
    fuseThreshold: input?.fuseThreshold ?? 50,
    armTime: input?.armTime ?? 5000,
  }),
  states: {
    intact: {
      on: {
        DAMAGE_RECEIVED: {
          actions: ['recordDamage'],
          target: 'armed',
          guard: 'shouldArm',
        },
      },
    },
    armed: {
      entry: ['startBeeping'],
      exit: ['stopBeeping'],
      on: {
        DAMAGE_RECEIVED: {
          actions: ['recordDamage'],
          target: 'exploding',
          guard: 'shouldExplode',
        },
      },
      after: {
        5000: 'intact',
      },
    },
    exploding: {
      entry: ['triggerExplosion', 'hideRenderMesh', 'spawnExplosiveDebris'],
      after: {
        500: 'debris',
      },
    },
    debris: {
      after: {
        2000: 'cleanup',
      },
    },
    cleanup: {
      entry: ['cleanup'],
      type: 'final',
    },
  },
});
